// Copyright (c) Docugami, Inc. All rights reserved.

import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { RequestState, useAsyncHelper } from "./AsyncHelper";

export interface OrganizationData<T> {
    fetch: () => void;
    state: RequestState;
    data: T;
    error?: Error;
}

/**
 * Helper hook for fetching data from the service for an organization.
 * @param friendlyName An identifiable name for the request
 * @param autoFetch Flag indicating if data should be fetched as soon as possible
 * @param getData Function that returns the fetched data
 * @param fetchData Function that fetches the data asynchronously
 */
export function useOrganizationDataRequest<T>(
    friendlyName: string,
    autoFetch: boolean,
    getData: () => T,
    fetchData: (orgId: string) => Promise<any>,
    canFetch: () => boolean) {
    const [currentData, setCurrentData] = useState<T>(getData());

    const currentOrgId = useSelector((state: AppState) => state.organizations.currentOrganization?.id);
    const { execute: executeAsync, state: requestState, error: requestError } = useAsyncHelper(friendlyName);

    const fetchFn = useCallback(() => {
        executeAsync(fetchData(currentOrgId));
    }, [currentOrgId, executeAsync, fetchData]);

    useEffect(() => {
        if (!!currentOrgId && autoFetch && canFetch()) {
            fetchFn();
        }
    },
    [autoFetch, currentOrgId, fetchFn, canFetch]);

    useEffect(() => {
        if (requestState === RequestState.Complete) {
            setCurrentData(getData());
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestState]);

    return {
        fetch: fetchFn,
        state: requestState,
        data: currentData,
        error: requestError
    };
}
